import React from "react";
import { CheckCircle } from "lucide-react";
import Bankingcycle from "./Bankingcycle";
import Footer from "../../Home/Footer";
import WhatsAppServices from "../../Home/WhatsAppServices";
import Faqs from "../../Home/Faqs";

// Array for dynamic data
const travelLeadData = {
  heading: "Transform Banking & Finance with WhatsApp",
  points: [
    "Simplify KYC with secure ID and document checks.",
    "Answer customer questions anytime, day or night.",
    "Show off your financial services with a WhatsApp storefront.",
  ],
  buttons: [
    { text: "Try ChatMitra for Growth", className: "bg-[#075E54] text-white hover:bg-[#064d40]",buttonLink:"https://app.chatmitra.com/signin" },
    { text: "Request a Live Demo", className: "border border-[#075E54] text-[#075E54] hover:text-white" },
  ],
  image: "https://chatmitra.s3.ap-south-1.amazonaws.com/chat-mitra-blog/Banking_Finance/Banking_Finance_1.png",
};


const bankingServices = [
  {
    id: 1,
    icon: <span className="text-2xl">💳</span>, // Credit card icon
    title: "Streamline Account Queries with WhatsApp",
    description: "Help customers check balances, report issues, and get instant answers via WhatsApp. It’s like having a 24/7 banker in their pocket—fast, secure, and super convenient!"
  },
  {
    id: 2,
    icon: <span className="text-2xl">🔒</span>, // Lock icon
    title: "Enhance Security with WhatsApp Alerts",
    description: "Send real-time fraud alerts, transaction notifications, and account updates through WhatsApp. Your clients will feel safer knowing you’re always watching out for them."
  },
  {
    id: 3,
    icon: <span className="text-2xl">📊</span>, // Chart icon
    title: "Boost Investment Engagement on WhatsApp",
    description: "Share market insights, portfolio updates, and personalized advice via WhatsApp. It’s the modern way to keep your high-value clients engaged and informed."
  },
  {
    id: 4,
    icon: <span className="text-2xl">💸</span>, // Money icon
    title: "Simplify Loan Applications via WhatsApp",
    description: "Let customers apply for loans, upload documents, and track progress all through WhatsApp. It’s hassle-free and speeds up approvals like never before."
  },
  {
    id: 5,
    icon: <span className="text-2xl">📅</span>, // Calendar icon
    title: "Schedule Appointments Easily on WhatsApp",
    description: "Help clients book consultations, branch visits, or financial planning sessions via WhatsApp. It’s a game-changer for customer convenience and retention."
  },
  {
    id: 6,
    icon: <span className="text-2xl">🤝</span>, // Handshake icon
    title: "Build Trust with Personalized WhatsApp Support",
    description: "Offer tailored advice, resolve disputes, and foster loyalty with direct WhatsApp chats. Your clients will appreciate the personal touch in a digital world."
  }
];

const bankingFAQs = [
  {
    question: "How can WhatsApp improve customer support for banks?",
    answer: "WhatsApp can revolutionize bank customer support by offering 24/7 instant responses to queries like account balances or transaction issues. It’s like having a personal banker in your pocket! With secure chats, you can also send alerts and updates, making customers feel safer and more connected."
  },
  {
    question: "Is WhatsApp secure enough for financial transactions?",
    answer: "Absolutely! WhatsApp uses end-to-end encryption, making it as secure as your bank’s app. You can share sensitive info like account details or send payment links safely, giving customers peace of mind while saving time for your team."
  },
  {
    question: "Can banks use WhatsApp to send transaction alerts?",
    answer: "Yes, and it’s a game-changer! Banks can send real-time fraud alerts, payment confirmations, and account updates via WhatsApp. It’s faster than email and more personal, keeping customers informed without overwhelming them."
  },
  {
    question: "How does WhatsApp help with loan applications?",
    answer: "WhatsApp simplifies loan processes by letting customers apply, upload documents, and track progress all in one chat. It’s like streamlining paperwork into a conversation—faster for you and easier for them!"
  },
  {
    question: "What kind of banking automation can WhatsApp handle?",
    answer: "WhatsApp can automate responses to common questions, schedule appointments, and even send reminders for bill payments. It’s like having a virtual assistant that works around the clock, freeing up your team for more complex tasks."
  },
  {
    question: "Can WhatsApp integrate with banking CRM systems?",
    answer: "Definitely! You can connect WhatsApp to your CRM to save customer data, track interactions, and assign follow-ups. It’s a seamless way to keep everything organized and boost efficiency."
  },
  {
    question: "How do customers feel about banking on WhatsApp?",
    answer: "Customers love it! They appreciate the convenience, speed, and personal touch of WhatsApp. It’s less formal than a call center and more immediate than email, making banking feel modern and user-friendly."
  },
  {
    question: "Can WhatsApp help banks reduce operational costs?",
    answer: "Yes, by automating routine tasks and reducing call center volume, WhatsApp lowers costs while improving service. It’s like getting more done with less effort—a win for both banks and customers."
  },
  {
    question: "What types of financial advice can be shared on WhatsApp?",
    answer: "You can send investment tips, market updates, and personalized advice via WhatsApp. It’s a direct line to engage high-value clients, keeping them informed and loyal with minimal effort."
  },
  {
    question: "How do I get started with WhatsApp for my bank?",
    answer: "Start by integrating WhatsApp Business API with your systems, train your team on secure chatting, and launch with simple features like support chats. Tools like ChatMitra can guide you every step of the way—it’s easier than you think!"
  },
];

const Banking = () => {
  return (
    <>
      <section className="max-w-6xl mx-auto px-4 py-16 bg-white pt-28">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="w-full lg:w-1/2">
            <h1 className="text-4xl font-bold text-gray-800 mb-6">Transform Banking & Finance with WhatsApp</h1>
            <ul className="space-y-4 text-gray-700">
              {travelLeadData.points.map((point, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="text-[#075E54] w-5 h-5 flex-shrink-0" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex gap-4">
              {travelLeadData.buttons.map((button, index) => (
               <a
               key={index}
               href={button.buttonLink}
               className={`py-3 px-6 rounded-lg text-lg font-semibold ${button.className}`}
             >
               {button.text}
             </a>
              ))}
            </div>
          </div>
          <div className="w-full lg:w-1/2">
            <img src={travelLeadData.image} alt="Banking & Finance Leads" className="w-full h-auto rounded-lg  hover:scale-105 transition-transform" />
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-8 text-center">
          <p className="text-lg text-gray-600">Make banking easier and friendlier with WhatsApp. Start with ChatMitra today—no credit card required!</p>
          <button className="mt-4 bg-[#075E54] text-white py-3 px-6 rounded-lg hover:bg-[#064d40] transition-colors">Get Start - No Credit Card Required</button>
        </div>
      </section>

      <Bankingcycle />
      <WhatsAppServices servicesData={bankingServices} isVisible={true}/>
      <Faqs faqs={bankingFAQs} />
      
      <Footer />

      <title>WhatsApp for Banking & Finance | ChatMitra</title>
      <meta name="description" content="Boost banking & finance with WhatsApp. Streamline KYC, offer 24/7 support, and engage customers with ChatMitra’s automation tools." />
      <meta name="keywords" content="WhatsApp for banking, finance customer support, BFSI WhatsApp, KYC automation, financial services engagement" />
      <meta property="og:title" content="WhatsApp for Banking & Finance | ChatMitra" />
      <meta property="og:description" content="Use WhatsApp to simplify KYC, answer queries anytime, and build trust with personalized banking services." />
      <meta property="og:url" content="https://blog.chatmitra.com/whatsapp-business-for-banking-and-finance" />
      <meta property="og:type" content="website" />
    </>
  );
};

export default Banking;